// Data Contracts
import {
  JudgeReviewFiling,
  JudgeReviewFilingConfiguration,
  JudgeReviewFilingMode,
} from '@data-contracts';

// Local
import { isEServiceRepresentation } from '@feature/filing/case-participant-list-table/case-participant-list-row.utils';
import { JudgeReviewServiceRecipient } from '../models/judge-review-recipient.view-model';
import { actionDistribution } from './judge-review-action.utils';
import { personDisplayName } from './person-display-name.util';

/**
 * Maps the distribution the judge chose into recipient-table UI rows. Shared by
 * the Confirmation page (before submit) and the Receipt (after submit), whose
 * per-page mappers delegate here. The config's `serviceParticipants` is the
 * original filing's already-sent service list, so it does not describe who the
 * judge's filing is served on; the pick lives on the in-progress action's distribution
 * as `filingMode` + `customServiceListParticipantIds`. Pure — no Angular dependencies.
 *
 * - `FileAndServe`: eService attorneys only.
 * - `CustomServiceList`: only the participants whose combination id was selected.
 * - Any other mode (file only / file to court): no service rows.
 *
 * Judge-entered additional recipients are appended whenever the filing is served.
 * One row is emitted per listed attorney. For a custom list, a participant with no
 * attorney still gets one row; for all eService parties it is left out.
 */
export function mapServedRecipients(
  filing: JudgeReviewFiling | null | undefined,
  config: JudgeReviewFilingConfiguration | null | undefined,
): JudgeReviewServiceRecipient[] {
  const distribution = actionDistribution(filing);
  const mode = distribution?.filingMode;
  const serviced =
    mode === JudgeReviewFilingMode.FileAndServe ||
    mode === JudgeReviewFilingMode.CustomServiceList;
  if (!serviced) {
    return [];
  }

  const serviceable = config?.serviceableParticipants ?? [];
  const selectedIds = new Set(
    distribution?.customServiceListParticipantIds ?? [],
  );
  const participants =
    mode === JudgeReviewFilingMode.CustomServiceList
      ? serviceable.filter(
          participant =>
            !!participant.caseParticipantUniqueId &&
            selectedIds.has(participant.caseParticipantUniqueId),
        )
      : serviceable;

  const rows: Omit<JudgeReviewServiceRecipient, 'key'>[] = [];
  for (const participant of participants) {
    const partyName = personDisplayName(participant.party);
    const partyType = participant.party?.partyType ?? '';
    const attorneys = participant.attorneys?.length
      ? participant.attorneys
      : [undefined];
    for (const attorney of attorneys) {
      if (
        mode === JudgeReviewFilingMode.FileAndServe &&
        !isEServiceRepresentation(attorney ?? null)
      ) {
        continue;
      }
      rows.push({
        partyName,
        partyType,
        attorneyName: attorney?.attorneyName ?? '',
        organizationName: attorney?.attorneyOrganizationName ?? '',
        deliveryMethod: attorney?.serviceMethod ?? '',
      });
    }
  }

  for (const recipient of distribution?.additionalRecipients ?? []) {
    rows.push({
      partyName: personDisplayName(recipient.name) || recipient.email || '',
      partyType: 'Additional recipient',
      attorneyName: '',
      organizationName: '',
      deliveryMethod: recipient.email ? 'Email' : '',
    });
  }

  return rows.map((row, index) => ({ ...row, key: String(index) }));
}
